import React, { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { getSubtabsFromChecklistResults } from 'app/pages/loanApplication/LoanApplication.utils';
import { generateAlertsSubtabPath } from 'app/services/loanNavigation';
import AccordionCDS from 'app/shared/componentsRestyled/coreDesignSystem/AccordionCDS';
import {
  LabelSize,
  LabelVariant,
} from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import { TableCDS } from 'app/shared/componentsRestyled/coreDesignSystem/Table';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { CHECKLIST_RECALCULATING_STATUSES } from 'constants/checklists';
import { FeatureFlag } from 'constants/featureFlags';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import useSort from 'hooks/useSort';
import { getConditionalArray, getConditionalObject } from 'utils/data';

import {
  getApplicationChecklistByIdAction,
  setCurrentChecklistIdAction,
} from 'store/loanApplications/actions/checklists.actions';
import { getLoanApplicationTasksAction } from 'store/loanApplications/actions/tasks.actions';
import {
  selectApplicationChecklistById,
  selectIsLoanChangesNotSaved,
  selectLoanApplicationChecklistResultsList,
} from 'store/loanApplications/selectors';
import { getUsersAction } from 'store/users/actions';

import { config, defaultSortOrder } from './Alerts.config';
import { AlertsToggleButtonsEnum, toggleButtons } from './Alerts.constants';
import { accordionCustomStyles, tableCustomStyles } from './Alerts.styles';
import { AlertsTestIds } from './Alerts.testIds';
import {
  filterCategories,
  getChecklistStatusLabelProps,
  mapItemsToRows,
  orderRows,
  resolveRedirectChecklistId,
} from './Alerts.utils';
import { AlertsActionContext } from './hooks/useAlertsActionContext/constants';
import SuppressedFields from './SuppressedFields';

const Alerts = ({ isLoading, setIsLoading }) => {
  const isLoanChangesNotSaved = useSelector(selectIsLoanChangesNotSaved);

  const [toggleButtonValue, setToggleButtonValue] = useState(
    isLoanChangesNotSaved
      ? AlertsToggleButtonsEnum.WITH_PENDING_CHANGES
      : AlertsToggleButtonsEnum.INITIAL_LOAN,
  );
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  const alertsActionContextValue = useMemo(
    () => ({ isActionInProgress, setIsActionInProgress }),
    [isActionInProgress],
  );

  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const { applicationId, subtab } = useParams();
  const loanId = useLoanId();
  const checklistResults = useSelector(selectLoanApplicationChecklistResultsList);
  const isReadChecklistsFromFlatStoreEnabled = useFeatureToggle(
    FeatureFlag.ReadChecklistsFromFlatStore,
  );

  const {
    checklist,
    statusDisplayType,
    calculatedResult: status,
    calculatedState,
    categoriesMap,
    itemsMap,
    pendingChangesCount,
    categoriesIdsWithPendingChanges,
    itemsWithPendingChanges,
    statusType,
  } = useSelector(selectApplicationChecklistById(subtab, toggleButtonValue));

  const { order, onRequestSort } = useSort(defaultSortOrder);
  const isActionButtonsDisabled =
    (isLoanChangesNotSaved && toggleButtonValue === AlertsToggleButtonsEnum.INITIAL_LOAN) ||
    isActionInProgress;
  const isChecklistRecalculating = CHECKLIST_RECALCULATING_STATUSES.includes(statusType);
  const availableSubtabs = useMemo(
    () => getSubtabsFromChecklistResults(checklistResults),
    [checklistResults],
  );
  const snapshotChecklistIds = useMemo(
    () => new Set(checklistResults.map(({ target }) => target?.checklistId).filter(Boolean)),
    [checklistResults],
  );
  const redirectChecklistId = resolveRedirectChecklistId({
    isLoading,
    subtab,
    snapshotChecklistIds,
    availableSubtabs,
  });

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    Promise.all([
      dispatch(
        getApplicationChecklistByIdAction({
          applicationId,
          includeResults: true,
          includeItems: true,
          includePendingChanges: true,
          checklistId: subtab,
        }),
      ),
      // FH-3004: the flat read resolves attribution display names BE-side onto each item
      // (overridenByName/pendingChangeByName), so only the legacy snapshots read still needs the
      // whole company's user list to resolve names on the FE.
      ...getConditionalArray(!isReadChecklistsFromFlatStoreEnabled, () => [
        dispatch(getUsersAction({ includeDeleted: true })),
      ]),
      dispatch(setCurrentChecklistIdAction(subtab)),
      loanId && dispatch(getLoanApplicationTasksAction()),
    ])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, applicationId, isReadChecklistsFromFlatStoreEnabled, loanId, setIsLoading, subtab]);

  useEffect(() => {
    if (!redirectChecklistId) {
      return;
    }

    history.replace({
      pathname: generateAlertsSubtabPath({ applicationId, checklistId: redirectChecklistId }),
      search,
    });
  }, [redirectChecklistId, history, applicationId, search]);

  useEffect(() => {
    setToggleButtonValue(
      isLoanChangesNotSaved
        ? AlertsToggleButtonsEnum.WITH_PENDING_CHANGES
        : AlertsToggleButtonsEnum.INITIAL_LOAN,
    );
  }, [isLoanChangesNotSaved]);

  const accordionProps = useMemo(
    () => ({
      customStyles: accordionCustomStyles,
      title: checklist?.name,
      isLoading,
      defaultExpanded: true,
      labelProps: {
        ...getChecklistStatusLabelProps({
          statusDisplayType,
          status,
          state: calculatedState,
          isChecklistRecalculating,
        }),
        size: LabelSize.L,
        isLoading,
        dataTestId: AlertsTestIds.CHECKLIST_STATUS_LABEL,
      },
      ...getConditionalObject(pendingChangesCount, {
        pendingChangesLabelProps: {
          variant: LabelVariant.ORANGE,
          pendingChangesCount,
          title: `${pendingChangesCount} Pending Change${pendingChangesCount === 1 ? '' : 's'}`,
          size: LabelSize.L,
          isLoading,
          dataTestId: AlertsTestIds.PENDING_CHANGES_LABEL,
        },
      }),
      ...getConditionalObject(isLoanChangesNotSaved, {
        pendingChangesToggleButtonProps: {
          buttons: toggleButtons,
          value: toggleButtonValue,
          onChange: setToggleButtonValue,
        },
      }),
    }),
    [
      checklist?.name,
      statusDisplayType,
      isLoading,
      status,
      calculatedState,
      isChecklistRecalculating,
      pendingChangesCount,
      isLoanChangesNotSaved,
      toggleButtonValue,
    ],
  );

  const data = useMemo(() => {
    if (!checklist) {
      return [];
    }

    const { categories, rootCategory } = filterCategories({
      categories: checklist.categories,
      categoriesMap,
      orderDirection: order.direction,
      itemsMap,
      orderId: order.id,
      disabled: isActionButtonsDisabled,
      statusDisplayType,
      categoriesIdsWithPendingChanges,
      itemsWithPendingChanges,
      isChecklistRecalculating,
    });

    const rootItems = rootCategory?.items?.length
      ? mapItemsToRows({
          items: rootCategory?.items,
          disabled: isActionButtonsDisabled,
          statusDisplayType,
          isChecklistRecalculating,
        })
      : [];

    const orderedCategories = orderRows({ rows: [...categories, ...rootItems], order });

    return orderedCategories;
  }, [
    categoriesMap,
    checklist,
    isChecklistRecalculating,
    isActionButtonsDisabled,
    itemsMap,
    order,
    statusDisplayType,
    categoriesIdsWithPendingChanges,
    itemsWithPendingChanges,
  ]);

  // A hidden checklist is a completeness decisioning engine (DEV-4352) whose content never
  // renders as an alert, even via a direct URL (DEV-5692) — only its suppressed-fields
  // accountability view stays reachable (the nav-badge deep link, DEV-5146).
  const isHiddenChecklist = !!checklist?.isHidden;
  // The checklist can be pruned from the store mid-session once the server stops returning it; with no
  // other subtab to fall back to there is nothing to accordion, and an untitled empty card reads broken.
  // The accordion is also the loading state, so it has to survive the window before the entity arrives.
  const isChecklistRenderable = !isHiddenChecklist && (isLoading || !!checklist);

  return (
    <AlertsActionContext.Provider value={alertsActionContextValue}>
      <SuppressedFields checklistId={subtab} isLoading={isLoading} />
      {isChecklistRenderable && (
        <AccordionCDS {...accordionProps}>
          <TableCDS
            isCustomDataDefinition
            withNoAvailableMessage
            config={config}
            customStyles={tableCustomStyles}
            data={data}
            isLoading={isLoading}
            pendingChangesTextSelector={null}
            withDivider={false}
            onRequestSort={onRequestSort}
          />
        </AccordionCDS>
      )}
    </AlertsActionContext.Provider>
  );
};

Alerts.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(Alerts);
